import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Mic, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'

interface VoiceRecordingModalProps {
  isOpen: boolean
  onClose: () => void
  onTranscript: (text: string) => void
  initialText?: string
}

export function VoiceRecordingModal({ isOpen, onClose, onTranscript, initialText = '' }: VoiceRecordingModalProps) {
  const [isListening, setIsListening] = useState(false)
  const { transcript, listening, resetTranscript } = useSpeechRecognition()
  const [fullTranscript, setFullTranscript] = useState(initialText)

  useEffect(() => {
    if (isOpen && !isListening) {
      // Start listening when modal opens
      setIsListening(true)
      setFullTranscript(initialText)
      resetTranscript()
      SpeechRecognition.startListening({ 
        language: 'de-DE', 
        continuous: true,
        interimResults: true 
      })
    }
  }, [isOpen, initialText])

  useEffect(() => {
    if (transcript) {
      // Update full transcript with new speech
      setFullTranscript(initialText + (initialText ? ' ' : '') + transcript)
    }
  }, [transcript, initialText])

  const handleClose = () => {
    SpeechRecognition.stopListening()
    setIsListening(false)
    
    // Pass the complete transcript back
    if (fullTranscript.trim()) {
      onTranscript(fullTranscript)
    }
    
    onClose()
  }

  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md"
            style={{ zIndex: 99999 }}
            onClick={handleClose}
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 100000 }}
          >
            <div className="pointer-events-auto">
              {/* Circular gradient background */}
              <div className="relative">
                <motion.div
                  className="absolute inset-0 rounded-full bg-gradient-radial from-primary-600/30 via-primary-600/10 to-transparent"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ width: '300px', height: '300px', transform: 'translate(-50%, -50%)', left: '50%', top: '50%' }}
                />

                {/* Microphone button */}
                <motion.button
                  onClick={handleClose}
                  className="relative w-32 h-32 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-2xl flex items-center justify-center group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Mic className="w-12 h-12 text-white" />
                  
                  {/* Pulsing ring animation when listening */}
                  {listening && (
                    <>
                      <motion.div
                        className="absolute inset-0 rounded-full border-4 border-red-400"
                        animate={{ scale: [1, 1.3, 1], opacity: [0.8, 0, 0.8] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                      <motion.div
                        className="absolute inset-0 rounded-full border-4 border-red-400"
                        animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                      />
                    </>
                  )}
                </motion.button>

                {/* Text display */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-8 text-center"
                >
                  <p className="text-white text-lg font-medium mb-2">
                    {listening ? 'Sprechen Sie jetzt...' : 'Verarbeite...'}
                  </p>
                  
                  {/* Show current transcript */}
                  {fullTranscript && (
                    <div className="mt-4 max-w-md mx-auto">
                      <p className="text-gray-300 text-sm bg-white/10 rounded-lg p-3 backdrop-blur">
                        {fullTranscript}
                      </p>
                    </div>
                  )}

                  <p className="text-gray-400 text-sm mt-4">
                    Klicken Sie auf das Mikrofon zum Beenden
                  </p>
                </motion.div>
              </div>

              {/* Close button in corner */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}